import { useState } from 'react';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignupForm } from '@/components/auth/SignupForm';
import { ConfirmSignup } from '@/components/auth/ConfirmSignup';
import { OrbBackground } from '@/components/auth/OrbBackground';
import ForGenAI from '@/components/auth/ForGenAI';
import logoIcon from '@/assets/loop-icon-short.png';
import PrismaticBurst from '@/components/auth/PrismaticBurst';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface LandingProps {
  onLoginSuccess: (token: string, username: string) => void;
}

type ViewType = 'login' | 'signup' | 'confirm';

export default function Landing({ onLoginSuccess }: LandingProps) {
  const [currentView, setCurrentView] = useState<ViewType>('login');
  const [signupUsername, setSignupUsername] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleSignupSuccess = (username: string) => {
    setSignupUsername(username);
    setCurrentView('confirm');
  };

  const handleSwitchToConfirm = () => {
    setSignupUsername(''); // Clear username so user can enter it manually
    setCurrentView('confirm');
  };

  const handleConfirmSuccess = () => {
    setShowConfirmModal(true);
  };

  const handleDismissConfirmModal = () => {
    setShowConfirmModal(false);
    setCurrentView('login');
  };

  return (
    <div className="min-h-screen w-full relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4 overflow-hidden">
      {/* Prismatic Burst Background */}
      <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
        <PrismaticBurst
          animationType="rotate3d"
          intensity={3}
          speed={0.5}
          distort={0.3}
          paused={false}
          offset={{ x: 0, y: 0 }}
          hoverDampness={0.25}
          rayCount={24}
          mixBlendMode="lighten"
          colors={['#F39C5E', '#FFDA76', '#49A1A7']}
          noiseAmount={0.1}
        />
      </div>

      {/* Animated Orb Background */}
      <OrbBackground />

      {/* Logo */}
      <div className="mb-4 flex flex-col items-center relative z-10 animate-fade-in">
        <div className="mb-2 transform hover:scale-105 transition-transform duration-300">
          {/* Title */}
          <div className="relative inline-block">
            <h1 className="text-6xl text-white text-center bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient-x" style={{ fontFamily: 'Baumans, cursive' }}>
              Patent Loop
            </h1>
            <span className="absolute -top-2 -right-16 px-3 py-1 text-xs font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-lg animate-pulse">
              BETA
            </span>
          </div>
          <p className="text-white/60 text-center mt-1 text-xl font-semibold tracking-wider drop-shadow-lg" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.05em' }}>
            AI-Powered Patent Analysis
          </p>

          {/* Logo Icon */}
          <div className="flex justify-center mt-2">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-pink-500/30 rounded-full blur-xl animate-pulse"></div>
              <img
                src={logoIcon}
                alt="Patent Loop"
                className="h-8 w-auto relative z-10 drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -top-10 -right-20 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {currentView === 'login' && (
          <LoginForm
            onLoginSuccess={onLoginSuccess}
            onSwitchToSignup={() => setCurrentView('signup')}
            onSwitchToConfirm={handleSwitchToConfirm}
          />
        )}

        {currentView === 'signup' && (
          <SignupForm
            onSwitchToLogin={() => setCurrentView('login')}
            onSignupSuccess={handleSignupSuccess}
            onSwitchToConfirm={handleSwitchToConfirm}
          />
        )}

        {currentView === 'confirm' && (
          <ConfirmSignup
            username={signupUsername}
            onConfirmSuccess={handleConfirmSuccess}
            onBackToLogin={() => setCurrentView('login')}
          />
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center relative z-10">
        <p className="text-white/60 text-sm">
          © 2026 Patent Loop. All rights reserved.
        </p>
        <p className="text-white/40 text-xs mt-2">
          For support, contact: support@patentloop.com
        </p>
      </div>

      {/* ForGen AI Logo */}
      <ForGenAI />

      {/* Account Confirmed Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDismissConfirmModal} />
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 max-w-md w-full animate-slide-up">
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 via-blue-500/20 to-purple-500/20 rounded-2xl -z-10" />

            <div className="text-center mb-6">
              <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
                Account Confirmed!
              </h2>
            </div>

            <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-5 mb-6">
              <p className="text-white text-center text-sm leading-relaxed">
                Your account has been confirmed successfully. You can now log in and start using Patent Loop.
              </p>
            </div>

            <Button
              onClick={handleDismissConfirmModal}
              className="w-full bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 hover:from-green-700 hover:via-blue-700 hover:to-purple-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300"
            >
              Continue to Login
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
